import React, { useState, useEffect } from "react";
import { FiX, FiUser, FiMail, FiPhone, FiMapPin, FiUsers } from "react-icons/fi";

const RELATIONSHIP_OPTIONS = [
  { value: "friend", label: "Friend" },
  { value: "family", label: "Family" },
  { value: "colleague", label: "Colleague" },
  { value: "client", label: "Client" },
  { value: "business", label: "Business" },
  { value: "other", label: "Other" },
];

const ContactModal = ({ isOpen, onClose, onSave, contact, isLoading }) => {
  const [formData, setFormData] = useState({
    name: "",
    relationship: "",
    work_email: "",
    personal_email: "",
    work_phone: "",
    personal_phone: "",
    address_1: "",
    address_2: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (contact) {
      setFormData({
        name: contact.name || "",
        relationship: contact.relationship || "",
        work_email: contact.work_email || "",
        personal_email: contact.personal_email || "",
        work_phone: contact.work_phone || "",
        personal_phone: contact.personal_phone || "",
        address_1: contact.address_1 || "",
        address_2: contact.address_2 || "",
      });
    } else {
      setFormData({
        name: "",
        relationship: "",
        work_email: "",
        personal_email: "",
        work_phone: "",
        personal_phone: "",
        address_1: "",
        address_2: "",
      });
    }
    setErrors({});
  }, [contact, isOpen]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    if (formData.work_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.work_email)) {
      newErrors.work_email = "Invalid email format";
    }
    if (formData.personal_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.personal_email)) {
      newErrors.personal_email = "Invalid email format";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  if (!isOpen) return null;

  const inputStyle = {
    backgroundColor: "var(--input-box-bg)",
    border: "1px solid var(--border-color)",
    borderRadius: "10px",
    padding: "10px 14px 10px 40px",
    color: "var(--text)",
    fontSize: "14px",
    width: "100%",
    transition: "all 0.2s ease",
  };

  const inputClassName = "contact-modal-input";

  const iconStyle = {
    position: "absolute",
    left: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "var(--teal-bg)",
    fontSize: "16px",
  };

  const labelStyle = {
    color: "var(--text)",
    fontSize: "12px",
    fontWeight: 600,
    marginBottom: "6px",
    display: "block",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    opacity: 0.8,
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "var(--background)",
          borderRadius: "16px",
          width: "100%",
          maxWidth: "600px",
          maxHeight: "90vh",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: "20px 24px",
            borderBottom: "1px solid var(--border-color)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h3 style={{ margin: 0, color: "var(--text)", fontWeight: 700, fontSize: "18px" }}>
            {contact ? "Edit Contact" : "Add New Contact"}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "8px",
              borderRadius: "8px",
              color: "var(--text)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div
            style={{
              padding: "24px",
              overflowY: "auto",
              maxHeight: "calc(90vh - 150px)",
            }}
          >
            <div className="row g-3">
              {/* Name */}
              <div className="col-md-6">
                <label style={labelStyle}>Name *</label>
                <div style={{ position: "relative" }}>
                  <FiUser style={iconStyle} />
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    placeholder="Contact name"
                    className={inputClassName}
                    style={{
                      ...inputStyle,
                      borderColor: errors.name ? "var(--danger)" : "var(--border-color)",
                    }}
                  />
                </div>
                {errors.name && (
                  <span style={{ color: "var(--danger)", fontSize: "12px" }}>{errors.name}</span>
                )}
              </div>

              {/* Relationship */}
              <div className="col-md-6">
                <label style={labelStyle}>Relationship</label>
                <div style={{ position: "relative" }}>
                  <FiUsers style={iconStyle} />
                  <select
                    value={formData.relationship}
                    onChange={(e) => handleChange("relationship", e.target.value)}
                    className={inputClassName}
                    style={{ ...inputStyle, cursor: "pointer" }}
                  >
                    <option value="">Select relationship</option>
                    {RELATIONSHIP_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Work Email */}
              <div className="col-md-6">
                <label style={labelStyle}>Work Email</label>
                <div style={{ position: "relative" }}>
                  <FiMail style={iconStyle} />
                  <input
                    type="email"
                    value={formData.work_email}
                    onChange={(e) => handleChange("work_email", e.target.value)}
                    placeholder="work@example.com"
                    className={inputClassName}
                    style={{
                      ...inputStyle,
                      borderColor: errors.work_email ? "var(--danger)" : "var(--border-color)",
                    }}
                  />
                </div>
                {errors.work_email && (
                  <span style={{ color: "var(--danger)", fontSize: "12px" }}>{errors.work_email}</span>
                )}
              </div>

              {/* Personal Email */}
              <div className="col-md-6">
                <label style={labelStyle}>Personal Email</label>
                <div style={{ position: "relative" }}>
                  <FiMail style={iconStyle} />
                  <input
                    type="email"
                    value={formData.personal_email}
                    onChange={(e) => handleChange("personal_email", e.target.value)}
                    placeholder="personal@example.com"
                    className={inputClassName}
                    style={{
                      ...inputStyle,
                      borderColor: errors.personal_email ? "var(--danger)" : "var(--border-color)",
                    }}
                  />
                </div>
                {errors.personal_email && (
                  <span style={{ color: "var(--danger)", fontSize: "12px" }}>{errors.personal_email}</span>
                )}
              </div>

              {/* Work Phone */}
              <div className="col-md-6">
                <label style={labelStyle}>Work Phone</label>
                <div style={{ position: "relative" }}>
                  <FiPhone style={iconStyle} />
                  <input
                    type="tel"
                    value={formData.work_phone}
                    onChange={(e) => handleChange("work_phone", e.target.value)}
                    placeholder="+1 234 567 8900"
                    className={inputClassName}
                    style={inputStyle}
                  />
                </div>
              </div>

              {/* Personal Phone */}
              <div className="col-md-6">
                <label style={labelStyle}>Personal Phone</label>
                <div style={{ position: "relative" }}>
                  <FiPhone style={iconStyle} />
                  <input
                    type="tel"
                    value={formData.personal_phone}
                    onChange={(e) => handleChange("personal_phone", e.target.value)}
                    placeholder="+1 234 567 8900"
                    className={inputClassName}
                    style={inputStyle}
                  />
                </div>
              </div>

              {/* Address Line 1 */}
              <div className="col-12">
                <label style={labelStyle}>Address Line 1</label>
                <div style={{ position: "relative" }}>
                  <FiMapPin style={iconStyle} />
                  <input
                    type="text"
                    value={formData.address_1}
                    onChange={(e) => handleChange("address_1", e.target.value)}
                    placeholder="Street address"
                    className={inputClassName}
                    style={inputStyle}
                  />
                </div>
              </div>

              {/* Address Line 2 */}
              <div className="col-12">
                <label style={labelStyle}>Address Line 2</label>
                <div style={{ position: "relative" }}>
                  <FiMapPin style={iconStyle} />
                  <input
                    type="text"
                    value={formData.address_2}
                    onChange={(e) => handleChange("address_2", e.target.value)}
                    placeholder="Apt, suite, city, state, zip"
                    className={inputClassName}
                    style={inputStyle}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              padding: "16px 24px",
              borderTop: "1px solid var(--border-color)",
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
            }}
          >
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: "10px 24px",
                borderRadius: "10px",
                border: "1px solid var(--border-color)",
                background: "var(--background)",
                color: "var(--text)",
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                padding: "10px 24px",
                borderRadius: "10px",
                border: "none",
                background: "linear-gradient(135deg, var(--teal-bg), #00b3a4)",
                color: "white",
                fontSize: "14px",
                fontWeight: 600,
                cursor: isLoading ? "not-allowed" : "pointer",
                opacity: isLoading ? 0.7 : 1,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {isLoading && (
                <span className="spinner-border spinner-border-sm" role="status" />
              )}
              {contact ? "Update Contact" : "Add Contact"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactModal;
